import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CreditCard, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ChargeCard({ booking, token, onSaved }) {
  const cof = booking.card_on_file;
  const [open, setOpen] = useState(false);
  const [desc, setDesc] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState(null);
  const [requestKey, setRequestKey] = useState("");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!open) return;
    setRequestKey(crypto.randomUUID());
  }, [open]);

  useEffect(() => {
    const amt = parseFloat(amount);
    if (!open || !amt || amt <= 0) { setQuote(null); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/payments/fee-quote`, { params: { subtotal: amt, method: "stripe" }, ...auth })
        .then((r) => setQuote(r.data)).catch(() => setQuote(null));
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, open]);

  if (!cof || !cof.last4) {
    return (
      <span data-testid={`no-card-on-file-${booking.id}`} className="text-xs text-white/35 flex items-center gap-1.5">
        <CreditCard size={13} /> No card on file
      </span>
    );
  }

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a charge amount greater than $0."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/charge-card`,
        { description: desc, amount: amt, request_key: requestKey }, { headers: { Authorization: `Bearer ${token}` } });
      if (data.status === "succeeded") {
        toast.success(`${money(data.charged)} charged to card ending ${data.last4} (subtotal ${money(data.subtotal)} + fee ${money(data.processing_fee)}).`);
        setOpen(false); setDesc(""); setAmount(""); setQuote(null);
        onSaved?.();
      } else {
        toast.error(`Charge status: ${data.status}. Please verify in Stripe.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not charge the saved card.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button data-testid={`charge-card-btn-${booking.id}`} onClick={() => setOpen(true)}
        className="text-xs flex items-center gap-1.5 border border-green-500/40 text-green-400 px-3 py-1.5 rounded hover:bg-green-500 hover:text-white transition-colors">
        <CreditCard size={13} /> Charge Card •••• {cof.last4}
      </button>
    );
  }
  return (
    <div data-testid={`charge-card-form-${booking.id}`} className="w-full mt-2 rounded-lg border border-green-500/30 bg-[#0A0A0A] p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-green-400 flex items-center gap-1.5">
          <CreditCard size={14} /> Charge {cof.brand ? `${cof.brand} ` : ""}•••• {cof.last4}
        </span>
        <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white"><X size={16} /></button>
      </div>
      <input data-testid={`charge-card-desc-${booking.id}`} value={desc} onChange={(e) => setDesc(e.target.value)}
        placeholder="Reason (e.g. dent repair, cleaning, ticket)" className="w-full bg-[#0C0C0C] border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-green-500/50" />
      <div className="flex gap-2">
        <input data-testid={`charge-card-amount-${booking.id}`} type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
          placeholder="Subtotal amount $" className="flex-1 bg-[#0C0C0C] border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-green-500/50" />
        <button data-testid={`charge-card-submit-${booking.id}`} onClick={submit} disabled={busy}
          className="bg-green-500 text-white font-semibold text-sm px-4 py-2 rounded hover:bg-green-600 transition-colors disabled:opacity-60 flex items-center gap-1.5">
          {busy && <Loader2 className="animate-spin" size={14} />} Charge Now
        </button>
      </div>
      {quote && (
        <div data-testid={`charge-card-quote-${booking.id}`} className="rounded-md border border-white/10 bg-black/30 p-2.5 text-xs space-y-1">
          <div className="flex justify-between text-white/60"><span>Subtotal</span><span>{money(quote.subtotal)}</span></div>
          <div className="flex justify-between text-white/60"><span>Processing Fee ({quote.percent}% + ${(quote.fixed_cents / 100).toFixed(2)})</span><span>{money(quote.processing_fee)}</span></div>
          <div className="flex justify-between text-white font-bold pt-1 border-t border-white/10"><span>Total Charged</span><span data-testid={`charge-card-total-${booking.id}`}>{money(quote.total_charged)}</span></div>
        </div>
      )}
      <p className="text-white/35 text-xs">Charges the saved card immediately (off-session), amount + AutoHire's card processing fee. Subtotal is marked as paid on this booking.</p>
    </div>
  );
}
